import { useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button } from '../../../../shared/components/index.js';

interface ArticleRecord {
  readonly knowledgeArticleId: string;
  readonly articleCode: string;
  readonly title: string;
  readonly category: string;
  readonly classification: string;
  readonly status: string;
  readonly version: string;
}

const CATEGORIES = ['PROCESS', 'PRODUCT', 'POLICY', 'TECHNICAL', 'COMPLIANCE', 'SALES', 'FINANCE', 'HR', 'OPERATIONS', 'CUSTOMER', 'TRAINING', 'GENERAL'];
const CLASSIFICATIONS = ['PUBLIC', 'INTERNAL', 'CONFIDENTIAL', 'RESTRICTED'];
const CONTENT_TYPES = ['MARKDOWN', 'HTML', 'PLAINTEXT', 'RICHTEXT', 'PDF', 'LINK'];

type Lifecycle = 'submit' | 'approve' | 'publish' | 'archive' | 'retire';

/**
 * M35 — Knowledge Article Editor (§33). Authoring workbench that registers a
 * new knowledge article (T462) with its descriptive metadata (title, summary,
 * category, security classification, body + content type) into the DRAFT state,
 * then drives it through the §14 approval workflow — submit for review, approve,
 * publish (BR-04 only APPROVED content publishes), archive and retire — via the
 * lifecycle action panel. Title and category are validated client-side; the
 * server enforces the authoritative rules and tenant isolation (LAW 4).
 */
export function KnowledgeArticleEditor(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState(CATEGORIES[0]);
  const [classification, setClassification] = useState(CLASSIFICATIONS[1]);
  const [contentType, setContentType] = useState(CONTENT_TYPES[0]);
  const [summary, setSummary] = useState('');
  const [content, setContent] = useState('');
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [article, setArticle] = useState<ArticleRecord | null>(null);

  const create = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setActionError(null);
    setArticle(null);
    if (!title.trim()) {
      setActionError(t('m35.article.titleRequired'));
      return;
    }
    if (!category) {
      setActionError(t('m35.article.categoryRequired'));
      return;
    }
    setBusy(true);
    try {
      const r = await apiRequest<{ data: ArticleRecord }>(`/v1/kms/articles`, {
        method: 'POST',
        body: {
          title: title.trim(),
          category,
          classification,
          contentType,
          summary: summary.trim() || undefined,
          content: content.trim() || undefined,
        },
        token,
      });
      setArticle(r.data);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError(t('m35.article.notAuthorisedAuthor'));
      } else {
        setActionError((err as Error).message);
      }
    } finally {
      setBusy(false);
    }
  };

  const runLifecycle = async (op: Lifecycle): Promise<void> => {
    if (!article) return;
    setActionError(null);
    setBusy(true);
    try {
      const r = await apiRequest<{ data: ArticleRecord }>(`/v1/kms/articles/${article.knowledgeArticleId}/${op}`, {
        method: 'POST',
        body: op === 'approve' ? {} : undefined,
        token,
      });
      setArticle(r.data);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError(t('m35.article.notAuthorisedOp', { op }));
      } else {
        setActionError((err as Error).message);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <main aria-label="article-editor">
      <h1>{t('m35.article.title')}</h1>
      <form aria-label="article-form" onSubmit={(e) => void create(e)}>
        <label>
          {t('m35.common.title')}
          <input aria-label="field-title" value={title} onChange={(e) => setTitle(e.target.value)} />
        </label>
        <label>
          {t('m35.common.category')}
          <select aria-label="field-category" value={category} onChange={(e) => setCategory(e.target.value)}>
            {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>
        <label>
          {t('m35.article.classification')}
          <select aria-label="field-classification" value={classification} onChange={(e) => setClassification(e.target.value)}>
            {CLASSIFICATIONS.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>
        <label>
          {t('m35.article.contentType')}
          <select aria-label="field-contenttype" value={contentType} onChange={(e) => setContentType(e.target.value)}>
            {CONTENT_TYPES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>
        <label>
          {t('m35.article.summary')}
          <textarea aria-label="field-summary" value={summary} onChange={(e) => setSummary(e.target.value)} />
        </label>
        <label>
          {t('m35.article.content')}
          <textarea aria-label="field-content" value={content} onChange={(e) => setContent(e.target.value)} />
        </label>
        <Button variant="primary" type="submit" aria-label="submit-article" disabled={busy}>
          {busy ? t('m35.common.saving') : t('m35.article.createBtn')}
        </Button>
      </form>

      {actionError && <p role="alert" aria-label="article-error">{actionError}</p>}
      {article && (
        <section aria-label="article-result">
          <dl role="status" aria-label="article-detail">
            <dt>{t('m35.article.resultCode')}</dt><dd aria-label="result-code">{article.articleCode}</dd>
            <dt>{t('m35.common.title')}</dt><dd aria-label="result-title">{article.title}</dd>
            <dt>{t('m35.common.status')}</dt><dd aria-label="result-status">{article.status}</dd>
            <dt>{t('m35.article.resultVersion')}</dt><dd aria-label="result-version">{article.version}</dd>
          </dl>
          <div className="lifecycle-actions" aria-label="lifecycle-actions">
            <Button variant="secondary" type="button" aria-label="action-submit" disabled={busy} onClick={() => void runLifecycle('submit')}>{t('m35.article.btnSubmit')}</Button>
            <Button variant="secondary" type="button" aria-label="action-approve" disabled={busy} onClick={() => void runLifecycle('approve')}>{t('m35.article.btnApprove')}</Button>
            <Button variant="primary" type="button" aria-label="action-publish" disabled={busy} onClick={() => void runLifecycle('publish')}>{t('m35.article.btnPublish')}</Button>
            <Button variant="danger" type="button" aria-label="action-archive" disabled={busy} onClick={() => void runLifecycle('archive')}>{t('m35.article.btnArchive')}</Button>
            <Button variant="danger" type="button" aria-label="action-retire" disabled={busy} onClick={() => void runLifecycle('retire')}>{t('m35.article.btnRetire')}</Button>
          </div>
        </section>
      )}
    </main>
  );
}
